import styled, { css } from 'styled-components'
import { Section, Section as SectionInterface } from '../../components/Section/Section'
import { Heading } from '../../components/Heading/Heading'
import { Subheading } from '../../components/Subheading/Subheading'

interface DynamicSectionProps extends SectionInterface {
  backgroundColor?: string
}

interface HeaderProps {
  textAlignment: 'left' | 'center'
}

interface TitleProps {
  color?: string
}

interface DescriptionProps {
  color?: string
}

interface CarouselSlideProps {
  itemGap: number
  itemsPerSlide: number
}

interface GridWrapperProps {
  itemsPerRow: number
  gap: number
  showAsCarouselOnMobile?: boolean
}

interface GridItemProps {
  itemsPerRow: number
  gap: number
}

interface CarouselSlideCardProps {
  itemsPerSlide: number
  itemGap: number
}

const SINGLE_CARD_REFERENCE_COLUMNS = 2

const singleCardTrack = (gap: number) => css`
  max-width: calc((100% - ${(SINGLE_CARD_REFERENCE_COLUMNS - 1) * gap}px) / ${SINGLE_CARD_REFERENCE_COLUMNS});
  margin-inline: auto;
`

export const DynamicSection = styled(Section) <DynamicSectionProps>`
  position: relative;
  z-index: 0;
  overflow: hidden;
  width: 100%;
  display: flex;
  justify-content: center;

  @media (min-width: 576px) {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  @media (min-width: 1216px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
`

export const DynamicContainer = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    max-width: 1216px;
  }
`

export const DynamicHeader = styled.div<HeaderProps>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;

  ${({ textAlignment }) => textAlignment === 'center' && css`
    text-align: center;
    align-items: center;
  `}

  @media (min-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`

export const DynamicTitle = styled(Heading) <TitleProps>`
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
  color: ${({ color }) => color || 'var(--text-title, #0f172a)'};
`

export const DynamicDescription = styled(Subheading) <DescriptionProps>`
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
  color: ${({ color }) => color || 'var(--text-body, #475569)'};
`

// Carousel Styles
export const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
`

export const CarouselContainer = styled.div`
  max-width: 1216px;
  width: 100%;
  display: flex;
  align-items: center;
`

export const CarouselViewport = styled.div`
  overflow: hidden;
  width: 100%;
`

export const CarouselContainerInner = styled.div<{ itemGap: number }>`
  display: flex;
  margin-left: ${({ itemGap }) => `-${itemGap}px`};

  @media (max-width: 767px) {
    margin-left: -1rem;
  }
`

export const CarouselSlide = styled.div<CarouselSlideProps>`
  flex-shrink: 0;
  flex-grow: 0;
  padding-left: ${({ itemGap }) => `${itemGap}px`};
  flex-basis: ${({ itemsPerSlide }) => `calc(100% / ${itemsPerSlide})`};

  @media (max-width: 767px) {
    flex-basis: calc(100% - 2rem);
    padding-left: 16px;
  }

  @media (max-width: 480px) {
    flex-basis: calc(100% - 1.5rem);
    padding-left: 12px;
  }
`

export const CarouselSlideCard = styled.div<CarouselSlideCardProps>`
  width: 100%;
  height: 100%;

  ${({ itemsPerSlide, itemGap }) => itemsPerSlide === 1 && css`
    @media (min-width: 768px) {
      ${singleCardTrack(itemGap)}
    }
  `}
`

export const CarouselPrev = styled.div`
  position: absolute;
  left: -4%;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  z-index: 10;

  @media (max-width: 767px) {
    display: none;
  }
`

export const CarouselNext = styled.div`
  position: absolute;
  right: -4%;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  z-index: 10;

  @media (max-width: 767px) {
    display: none;
  }
`

export const CarouselProgress = styled.div`
  width: 100%;
  margin-top: 1.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;

  @media (min-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`

// Grid Styles
export const GridWrapper = styled.div<GridWrapperProps>`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(${({ itemsPerRow }) => itemsPerRow}, 1fr);
  gap: ${({ gap }) => `${gap}px`};
  padding-left: 1.5rem;
  padding-right: 1.5rem;

  @media (max-width: 1024px) {
    ${({ itemsPerRow }) => itemsPerRow > 1 && css`
      grid-template-columns: repeat(3, 1fr);
      gap: 24px;
    `}
  }

  @media (max-width: 767px) {
    ${({ showAsCarouselOnMobile }) => showAsCarouselOnMobile
    ? css`
        display: none;
      `
    : css`
        grid-template-columns: 1fr;
        gap: 16px;
      `
  }
  }

  @media (min-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`

export const GridItem = styled.div<GridItemProps>`
  width: 100%;

  ${({ itemsPerRow, gap }) => itemsPerRow === 1 && css`
    @media (min-width: 768px) {
      ${singleCardTrack(gap)}
    }
  `}
`

// Mobile Carousel (for grid mode with showAsCarouselOnMobile)
export const MobileCarouselWrapper = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    gap: 1rem;

    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`

export const MobileCarouselItem = styled.div`
  flex-shrink: 0;
  width: calc(100% - 3rem);
  scroll-snap-align: start;

  @media (max-width: 480px) {
    width: calc(100% - 2rem);
  }
`

// Carousel mode wrapper (for desktop)
export const CarouselModeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: 767px) {
    padding-left: 1.5rem;
  }
`

// Background styles
interface BackgroundShadowProps {
  shadowColor?: string
}

interface BackgroundColorProps {
  backgroundcolor?: string
}

interface ButtonProps {
  buttonBgColor?: string
  buttonTextColor?: string
  buttonBorderColor?: string
}

export const BackgroundShadow = styled.div<BackgroundShadowProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background-color: ${({ shadowColor }) => shadowColor || 'rgba(0, 0, 0, 0.25)'};
`

export const BackgroundImage = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  .desktop-image,
  .mobile-image {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    object-fit: cover;
  }

  .desktop-image {
    @media (max-width: 767px) {
      display: none;
    }
  }

  .mobile-image {
    @media (min-width: 768px) {
      display: none;
    }
  }
`

export const BackgroundColor = styled.div<BackgroundColorProps>`
  position: absolute;
  inset: 0;
  background-color: ${({ backgroundcolor }) => backgroundcolor || "var(--general-background, #f8fafc)"};
`

export const Buttons = styled.div`
  margin-top: 2.5rem;
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media (max-width: 767px) {
    flex-direction: column;
    padding: 0 1.5rem;
  }
`

export const ButtonItem = styled.a<ButtonProps>`
  text-decoration: none;
  background-color: ${({ buttonBgColor }) => buttonBgColor || "var(--buttons-background, #3b82f6)"};
  color: ${({ buttonTextColor }) => buttonTextColor || "var(--buttons-text, #ffffff)"};
  border: var(--design-button-border-width, 1px) solid ${({ buttonBorderColor }) => buttonBorderColor || "var(--buttons-border, #3b82f6)"};
  border-radius: var(--design-button-border-radius, 0.5rem);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  font-weight: 500;

  @media (max-width: 767px) {
    width: 100%;
  }

`
